import { useEffect } from 'react';
import { useEventTracking } from '@services/statsig';

import type { GuideSurfaceId } from '../constants/featureLinks';
import type { GuideLinkShape } from '../types';

export type GuideLinkTrackingArgs = {
  surfaceId: GuideSurfaceId;
  shape: GuideLinkShape;
  /** The article the link renders, as its canonical slug and topic; undefined while it renders nothing. */
  article: { slug: string; topic: string } | undefined;
};

/**
 * Surfaces that already emitted `shown` or `hovered` on the current page view.
 * Module state, not component state, so the count survives a remount (the
 * Documents, Labs and Forms pages pass their empty state as an inline
 * component that remounts on every re-render).
 *
 * A page view is a navigation: PageViewObserver, mounted in the authenticated
 * layout, calls `startPageView` on every route change, including a return to
 * a pathname visited earlier. Without an observer (tests, stories) a view is
 * a pathname, read when a link tracks.
 */
const pageView = { view: '', emitted: new Set<string>() };
let navigations = 0;

/** Starts a new page view; the router observer calls it on every navigation. */
export function startPageView(): void {
  navigations += 1;
  pageView.view = `${navigations}:${window.location.pathname}`;
  pageView.emitted.clear();
}

/** True the first time `key` is seen on the current page view. */
function firstOnPageView(key: string): boolean {
  if (navigations === 0 && pageView.view !== window.location.pathname) {
    pageView.view = window.location.pathname;
    pageView.emitted.clear();
  }
  if (pageView.emitted.has(key)) return false;
  pageView.emitted.add(key);
  return true;
}

/**
 * The shown, hovered and clicked events (GDN-3705, AC-15 and AC-16), emitted
 * from the GuideLink container: `shown` once per surface per page view when a
 * shape renders, `hovered` once per surface per page view when the tooltip
 * opens by hover or keyboard focus, `clicked` on every activation.
 */
export function useGuideLinkTracking({ surfaceId, shape, article }: GuideLinkTrackingArgs) {
  const { featureLinks } = useEventTracking();
  const slug = article?.slug;
  const topic = article?.topic;
  useEffect(() => {
    if (!slug || !topic || !firstOnPageView(`shown:${surfaceId}`)) return;
    featureLinks.shown({ surface_id: surfaceId, article_slug: slug, topic, shape });
  }, [featureLinks, surfaceId, shape, slug, topic]);
  const metadata = article && {
    surface_id: surfaceId,
    article_slug: article.slug,
    topic: article.topic,
    shape,
  };
  return {
    hovered: () => {
      if (metadata && firstOnPageView(`hovered:${surfaceId}`)) featureLinks.hovered(metadata);
    },
    clicked: () => {
      if (metadata) featureLinks.clicked(metadata);
    },
  };
}
